import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, Check, Send, Loader2, CheckCircle2, Phone, CalendarDays, Upload, AlertTriangle, IdCard, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CARS, CONTACT } from "@/lib/data";
import { Reveal } from "@/lib/motion";

const DAY_MS = 86400000;
const todayStr = () => new Date().toISOString().slice(0, 10);
const toDate = (s) => (s ? new Date(s + "T00:00:00") : null);
const addDays = (s, n) => {
  const d = toDate(s);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const diffDays = (a, b) => Math.round((toDate(b) - toDate(a)) / DAY_MS);

export const Booking = () => {
  const [carId, setCarId] = useState(CARS[0].id);
  const [days, setDays] = useState(CARS[0].minDays);
  const [pickup, setPickup] = useState("");
  const [ret, setRet] = useState("");
  const [drivesFor, setDrivesFor] = useState("");
  const [license, setLicense] = useState({ id: "", status: "", name: "", verified: false, readable: true, uploading: false });
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const onSelect = (e) => selectCar(e.detail);
    window.addEventListener("orl:selectCar", onSelect);
    return () => window.removeEventListener("orl:selectCar", onSelect);
  }, []);

  const car = CARS.find((c) => c.id === carId);
  const minDays = car.minDays;
  const rental = car.price * days;
  const total = rental + car.deposit;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const selectCar = (id) => {
    const c = CARS.find((x) => x.id === id);
    if (!c) return;
    setCarId(id);
    setDays((d) => Math.max(d, c.minDays));
  };

  // Keep the return date in sync with pickup + day count (per-car minimum enforced)
  useEffect(() => {
    if (pickup) setRet(addDays(pickup, days));
  }, [days, pickup]);

  const applyDays = (n) => {
    const d = Math.max(minDays, Math.min(365, n || minDays));
    setDays(d);
  };

  const onPickup = (v) => {
    setPickup(v);
    if (!v) return;
    if (ret) {
      const d = diffDays(v, ret);
      if (d >= minDays) setDays(d);
    }
  };

  const onReturn = (v) => {
    if (!pickup) {
      setRet(v);
      return;
    }
    const d = diffDays(pickup, v);
    if (d >= minDays) {
      setRet(v);
      setDays(d);
    } else {
      toast.info(`${minDays}-day minimum rental for the ${car.name}`);
    }
  };

  const onLicense = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setLicense((l) => ({ ...l, uploading: true }));
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", form.name);
    try {
      const res = await api.post("/license/verify", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const readable = res.data.readable !== false;
      setLicense({ id: res.data.id, status: res.data.status, name: res.data.name, verified: res.data.verified, readable, uploading: false });
      if (!readable) toast.warning("We couldn't read that photo — please retake it.");
      else toast.success("License received");
    } catch (err) {
      setLicense((l) => ({ ...l, uploading: false }));
      toast.error(err?.response?.data?.detail || "Could not upload the license, please retry.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) return toast.error("Please enter your name and phone.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return toast.error("Please enter a valid email.");
    if (!license.id) return toast.error("Please upload your driver's license for approval.");
    setSubmitting(true);
    try {
      const res = await api.post("/bookings", {
        ...form,
        car: carId,
        days,
        drives_for: drivesFor,
        pickup_date: pickup,
        return_date: ret,
        license_id: license.id,
      });
      setResult(res.data);
      toast.success("Request sent! We'll be in touch shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please call or text us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="book" data-testid="booking-section" className="bg-white py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="max-w-2xl mb-12">
          <p className="text-gold-dark text-xs font-bold uppercase tracking-[0.3em] mb-4">Build Your Rental</p>
          <h2 className="font-heading font-bold text-navy text-4xl sm:text-5xl lg:text-[3.4rem] tracking-tight leading-[1.02]">
            Get your instant quote.
          </h2>
          <p className="mt-5 text-muted-foreground text-base md:text-lg leading-relaxed">
            Pick a car and choose your dates — see your total instantly. 3-day minimum on the Tesla, 4-day on the Hyundai.
          </p>
        </Reveal>

        {result ? (
          <Reveal>
            <div data-testid="booking-success" className="max-w-2xl rounded-3xl border border-gold/40 bg-gold/5 p-8 md:p-10 text-center">
              <CheckCircle2 className="w-16 h-16 text-gold-dark mx-auto" />
              <h3 className="mt-4 font-heading font-bold text-2xl text-navy">Request received!</h3>
              <p className="mt-2 text-muted-foreground">
                Thanks {form.name.split(" ")[0]} — we've sent your {result.car_name} request ({result.days} days) to our team.
                Estimated total due at pickup: <strong className="text-navy">${result.total_due}</strong>. We'll reach out to {form.phone} shortly.
              </p>
              <a href={`tel:${CONTACT.phoneRaw}`} className="mt-6 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-white font-semibold hover:bg-navy-light transition-colors">
                <Phone className="w-4 h-4" /> Or call us now
              </a>
            </div>
          </Reveal>
        ) : (
          <div className="grid lg:grid-cols-5 gap-8 items-start">
            {/* Builder */}
            <form onSubmit={submit} className="lg:col-span-3 space-y-8">
              {/* Car selection */}
              <div>
                <Label className="text-navy font-semibold text-sm uppercase tracking-wider">1. Choose your car</Label>
                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                  {CARS.map((c) => (
                    <button
                      type="button"
                      key={c.id}
                      data-testid={`book-car-${c.id}`}
                      onClick={() => selectCar(c.id)}
                      className={`flex items-center gap-3 rounded-2xl border-2 p-3 text-left transition-all ${
                        carId === c.id ? "border-gold bg-gold/5" : "border-navy/10 hover:border-navy/30"
                      }`}
                    >
                      <img src={c.images[0]} alt={c.name} className="w-20 h-16 object-cover rounded-lg shrink-0" />
                      <div>
                        <p className="font-heading font-bold text-navy leading-tight">{c.name}</p>
                        <p className="text-gold-dark font-bold text-sm">${c.price}/day</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Rental dates */}
              <div>
                <Label className="text-navy font-semibold text-sm uppercase tracking-wider">2. Rental dates</Label>
                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                  <div>
                    <span className="text-xs text-muted-foreground flex items-center gap-1 mb-1"><CalendarDays className="w-3.5 h-3.5" /> Pickup date</span>
                    <Input data-testid="book-pickup" type="date" min={todayStr()} value={pickup} onChange={(e) => onPickup(e.target.value)} className="h-12 border-navy/20 text-navy" />
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground flex items-center gap-1 mb-1"><CalendarDays className="w-3.5 h-3.5" /> Return date</span>
                    <Input data-testid="book-return" type="date" min={pickup ? addDays(pickup, minDays) : todayStr()} value={ret} onChange={(e) => onReturn(e.target.value)} className="h-12 border-navy/20 text-navy" />
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-4">
                  <div className="flex items-center rounded-full border-2 border-navy/10 overflow-hidden">
                    <button type="button" data-testid="book-days-minus" onClick={() => applyDays(days - 1)} className="w-11 h-11 flex items-center justify-center text-navy hover:bg-secondary disabled:opacity-40" disabled={days <= minDays}>
                      <Minus className="w-4 h-4" />
                    </button>
                    <input
                      data-testid="book-days-input"
                      type="number"
                      min={minDays}
                      value={days}
                      onChange={(e) => applyDays(parseInt(e.target.value, 10))}
                      className="w-14 text-center font-heading font-bold text-xl text-navy outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <button type="button" data-testid="book-days-plus" onClick={() => applyDays(days + 1)} className="w-11 h-11 flex items-center justify-center text-navy hover:bg-secondary">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <span className="text-muted-foreground text-sm">total days · {minDays}-day minimum</span>
                </div>
              </div>

              {/* Currently driving for */}
              <div>
                <Label className="text-navy font-semibold text-sm uppercase tracking-wider">3. Do you currently drive for Uber or Lyft?</Label>
                <div className="mt-3 flex flex-wrap gap-2.5">
                  {["Uber", "Lyft", "Both", "Not currently"].map((opt) => (
                    <button
                      type="button"
                      key={opt}
                      data-testid={`book-drives-${opt.toLowerCase().replace(/ /g, "-")}`}
                      onClick={() => setDrivesFor(opt)}
                      className={`rounded-full border-2 px-5 py-2.5 text-sm font-semibold transition-all ${
                        drivesFor === opt ? "border-gold bg-gold/10 text-navy" : "border-navy/10 text-navy/70 hover:border-navy/30"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Driver's license */}
              <div>
                <Label className="text-navy font-semibold text-sm uppercase tracking-wider">4. Driver's license</Label>
                <p className="text-xs text-muted-foreground mt-1">Upload a clear photo for approval — we verify it before contacting you.</p>
                <label
                  htmlFor="license-file"
                  data-testid="book-license-drop"
                  className={`mt-3 flex items-center gap-4 rounded-2xl border-2 border-dashed p-4 cursor-pointer transition-all ${
                    license.id ? "border-gold bg-gold/5" : "border-navy/20 hover:border-navy/40"
                  }`}
                >
                  <span className="w-12 h-12 rounded-xl bg-navy flex items-center justify-center shrink-0">
                    {license.uploading ? <Loader2 className="w-5 h-5 text-gold animate-spin" /> : license.id ? (license.verified ? <CheckCircle2 className="w-5 h-5 text-gold" /> : <AlertTriangle className="w-5 h-5 text-gold" />) : <IdCard className="w-5 h-5 text-gold" />}
                  </span>
                  <div className="min-w-0">
                    {license.uploading ? (
                      <p className="font-semibold text-navy">Verifying your license…</p>
                    ) : license.id ? (
                      <>
                        <p className="font-semibold text-navy">{license.status}{license.name ? ` · ${license.name}` : ""}</p>
                        <p className="text-xs text-muted-foreground">Tap to replace the photo</p>
                      </>
                    ) : (
                      <>
                        <p className="font-semibold text-navy flex items-center gap-1.5"><Upload className="w-4 h-4" /> Upload driver's license *</p>
                        <p className="text-xs text-muted-foreground">JPG or PNG, front of the license</p>
                      </>
                    )}
                  </div>
                </label>
                <input id="license-file" data-testid="book-license-input" type="file" accept="image/*" capture="environment" onChange={onLicense} className="hidden" />
                {license.id && !license.readable && (
                  <div data-testid="license-retake-card" className="mt-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1">
                      <p className="font-semibold text-navy text-sm flex items-center gap-1.5">
                        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" /> We couldn't read that photo clearly
                      </p>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        No worries — try again: lay the license flat, use good lighting, and make sure all 4 corners are visible.
                      </p>
                    </div>
                    <label
                      htmlFor="license-file"
                      data-testid="license-retake-btn"
                      className="inline-flex items-center justify-center gap-1.5 rounded-full bg-navy px-5 py-2.5 text-white text-sm font-semibold cursor-pointer hover:bg-navy-light transition-colors shrink-0"
                    >
                      <Camera className="w-4 h-4" /> Retake photo
                    </label>
                  </div>
                )}
              </div>

              {/* Contact */}
              <div>
                <Label className="text-navy font-semibold text-sm uppercase tracking-wider">5. Your details</Label>
                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                  <Input data-testid="book-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Full name *" className="h-12 border-navy/20" />
                  <Input data-testid="book-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="Phone *" className="h-12 border-navy/20" />
                  <Input data-testid="book-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="Email *" className="h-12 border-navy/20 sm:col-span-2" />
                </div>
                <Textarea data-testid="book-message" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Anything else? (optional)" rows={2} className="mt-3 border-navy/20 resize-none" />
              </div>
            </form>

            {/* Summary */}
            <div className="lg:col-span-2 lg:sticky lg:top-28">
              <motion.div layout className="rounded-3xl bg-navy text-white p-7 md:p-8 shadow-2xl">
                <p className="text-gold text-xs font-bold uppercase tracking-[0.25em]">Your quote</p>
                <p className="mt-1 font-heading font-bold text-2xl">{car.name}</p>
                {pickup && ret && (
                  <p className="mt-1 text-white/60 text-sm flex items-center gap-1.5">
                    <CalendarDays className="w-4 h-4 text-gold" /> {pickup} → {ret}
                  </p>
                )}

                <div className="mt-6 space-y-3 text-sm">
                  <Row label={`$${car.price}/day × ${days} days`} value={`$${rental}`} />
                  <Row label="Refundable deposit" value={`$${car.deposit}`} muted />
                  <div className="h-px bg-white/15 my-2" />
                  <div className="flex items-baseline justify-between">
                    <span className="text-white/80">Total due at pickup</span>
                    <span data-testid="book-total" className="font-heading font-extrabold text-gold text-3xl">${total}</span>
                  </div>
                  <p className="text-white/50 text-xs">Deposit is refunded if not used toward tolls or incidentals.</p>
                  <p className="text-white/50 text-xs mt-1">Includes 200 miles/day · rentals stay in Central FL (Orange, Osceola, Lake, Seminole &amp; Sumter counties).</p>
                </div>

                <Button
                  type="submit"
                  onClick={submit}
                  data-testid="book-submit"
                  disabled={submitting}
                  className="mt-6 w-full h-12 rounded-full bg-gold text-navy font-bold text-base hover:bg-gold-dark transition-colors"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Send My Request</>}
                </Button>
                <p className="mt-3 text-center text-white/50 text-xs flex items-center justify-center gap-1">
                  <Check className="w-3 h-3" /> No credit card required to inquire
                </p>
              </motion.div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const Row = ({ label, value, muted }) => (
  <div className="flex items-baseline justify-between">
    <span className={muted ? "text-white/50" : "text-white/80"}>{label}</span>
    <span className="font-semibold">{value}</span>
  </div>
);
